import { BlogPost, BLOG_AUTHOR } from '../../../types/blogTypes';

const post: BlogPost = {
  slug: 'english-mistakes-fluent-speakers-make',
  title: '10 Common English Mistakes and a One-Line Fix for Each',
  description:
    'Fix 10 common English mistakes, from its vs it\'s to could of, with a wrong-and-right example and a one-line memory trick for each.',
  category: 'Language',
  icon: 'spell-check',
  publishedAt: '2026-10-28',
  author: BLOG_AUTHOR,
  product: 'chot-buddies',
  buddy: 'English Teacher',
  keywords: ['common english mistakes', 'english grammar mistakes', 'commonly confused words'],
  body: `You typed "their going to be late," hit send, and spotted the mistake three seconds later. Too late. These common English mistakes trip up native speakers every day, including people who write for a living.

English spells words that sound alike in different ways, and most of us learn it by ear first. If you are learning English, you are in good company. If you grew up speaking it, so are you. Below are ten mistakes, each with the wrong version, the right version and a one-line trick to keep it straight. Skim the list in about five minutes, then pick the two that look familiar.

## 1. Its or it's?

- **Wrong:** The dog wagged it's tail.
- **Right:** The dog wagged its tail.

**Memory trick:** "It's" always means "it is" or "it has," so if you cannot swap one of those in, drop the apostrophe.

## 2. Their, there or they're?

- **Wrong:** Their going to meet us over they're.
- **Right:** They're going to meet us over there.

**Memory trick:** "There" has "here" hiding inside it, so it points to a place, "they're" is "they are," and "their" is the one that shows who owns something.

## 3. Then or than?

- **Wrong:** My brother is taller then me.
- **Right:** My brother is taller than me.

**Memory trick:** "Than" compares two things, while "then" is about time and rhymes with "when."

## 4. Affect or effect?

- **Wrong:** The weather effects my mood, and the affect is obvious.
- **Right:** The weather affects my mood, and the effect is obvious.

**Memory trick:** A is for action and E is for end result, so "affect" is usually the verb (to influence) and "effect" is usually the noun (the result).

## 5. Fewer or less?

- **Wrong:** There are less cars on the road today.
- **Right:** There are fewer cars on the road today.

**Memory trick:** If you can count the things one by one, use "fewer," and if you can only measure it, use "less," as in less traffic or less water.

## 6. Could of

- **Wrong:** I could of called you.
- **Right:** I could have called you.

**Memory trick:** "Could've" sounds like "could of" out loud, but "of" is never a helping verb, so write "have" every time (the same goes for would, should and might).

## 7. For all intents and purposes

- **Wrong:** For all intensive purposes, the project is finished.
- **Right:** For all intents and purposes, the project is finished.

**Memory trick:** The phrase is a matched pair of plain words, intents and purposes, and "intensive" has no place in it.

## 8. I could care less

- **Wrong:** I could care less what he thinks.
- **Right:** I couldn't care less what he thinks.

**Memory trick:** If you could care less, you still care a little, so say "couldn't" when you mean you have hit zero. You will hear the first version a lot in casual speech, but the second is the standard form and the one that makes sense.

## 9. Lose or loose?

- **Wrong:** I don't want to loose my keys, and these jeans are too lose.
- **Right:** I don't want to lose my keys, and these jeans are too loose.

**Memory trick:** "Loose" has extra room and an extra o, while "lose" is the one that lost an o.

## 10. Me and him, between you and I

- **Wrong:** Me and him went to the game.
- **Right:** He and I went to the game.
- **Wrong:** This is between you and I.
- **Right:** This is between you and me.

**Memory trick:** Drop the other person and listen: "Me went to the game" sounds off, so use "I," and "between I" sounds off, so use "me." Many people overcorrect to "I" because it feels more polished, which is why "between you and I" is so common.

## Turn common English mistakes into progress

A mistake is not a verdict on your English. It tells you exactly what to practice next. Catch each one and record it, so you fix it once instead of every week.

Keep a mistakes notebook, on paper or in your phone. It takes about two minutes per entry:

1. Write the sentence the way you wrote it, wrong version and all.
2. Write the corrected sentence right beside it.
3. Add one line on why, or your own memory trick.
4. Once a week, cover the right-hand side and try to fix each wrong sentence from memory.

That last step matters most. Recalling the answer teaches you more than rereading it.

Want a practice partner? [Chot Buddies](/products/chot-buddies/) has the English Teacher, a buddy that teaches English only. It never tires of explaining the same rule, so you can ask the same question as many times as you need without feeling silly. Paste one of these:

> I keep mixing up its and it's, and could of and could have. Quiz me with ten short sentences, one at a time, and explain why each answer is right after I reply.

> Here are mistakes from my notebook: [paste your list]. Make me a mixed practice round, and when I get one wrong, give me a new memory trick for it.

Worried about getting things wrong in front of someone? [Learn a Language Without Feeling Embarrassed](/blog/learn-a-language-without-feeling-embarrassed/) has ideas for that. For more ways to build speaking confidence, read [Learn English Faster: Start Speaking on Day One](/blog/learn-english-faster-speak-from-day-one/).

## Fluent does not mean flawless

Every fluent speaker has typed "their" when they meant "they're." The people who keep improving notice the slip, write it down and try again. Pick two mistakes from this list and add them to your notebook today.

Then open Chot Buddies, which is free to start, and ask the English Teacher to quiz you on those two with ten short sentences. Ten sentences take about five minutes.
`,
};

export default post;
